import { definedMap } from "@glideapps/ts-necessities";
import entries from "lodash/entries";
import last from "lodash/last";
import type {
    ActionPropertyDescriptor,
    AllowedDataEdits,
    AppDescriptionContext,
    ColumnPropertyDescriptorCase,
    ComponentBuilder,
    ComponentDescriptor,
    ComponentErrorAndLink,
    ComponentPrerequisite,
    ComponentSpecialCaseDescriptor,
    EditedColumnsAndTables,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
    StringPropertyDescriptorCase,
} from ".";
import {
    combineEditedColumnsAndTables,
    convertEditedColumnsToIndirect,
    getPropertyDescription,
    mapActionsRecord,
    wrapActionProperties,
} from ".";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import type { MinimalAppEnvironment, QuotaBannerRequirements, ValueProducer } from "../../common/components/types";
import type {
    ComponentDescription,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    getInputOrOutputTable,
    getTableColumnDisplayName,
    makeEmptyComponentDescription,
    PropertyKind,
} from "../../common/description";
import { checkString, defined, panic, updateDeleteUndefined } from "../../common/Support";
import { handlerForActionKind, rewriteActions } from "../actions";
import { getPropertyDescriptorCaseForKind } from "../description-utils";
import { duplicateDescription } from "../handlers";
import type { SerializedElementBuilder } from "../serialized-component";
import { resolveSourceColumn } from "../source-column";
import type { ComponentHandler } from "./component-handler";
import { getActionsForComponent } from "./component-utils";
import { PopulationMode } from "./description-handlers";
import { getColumnsUsedInDescription, getEditedColumnsForProperties } from "./descriptor-utils";
import { populateDescription } from "./populate-description";
import { findCaptionDescriptor, visibilityPropertyDescriptor } from "./property-handlers";
import { getSourceColumnProperty, getStringProperty } from "./property-utils";
import type { WireRowComponentHydrator, WireRowComponentInflationBackend } from "./wire";

export abstract class ComponentHandlerBase<T extends ComponentDescription> implements ComponentHandler<T> {
    constructor(public readonly kind: ComponentKind) {}

    protected getBasePropertyDescriptors(): PropertyDescriptor[] {
        return [visibilityPropertyDescriptor];
    }

    // table might be `undefined` in cases where we want to find the caption or action descriptors.
    public abstract getDescriptor(
        desc: T | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screenContext?: ValueProducer,
        appEnvironment?: MinimalAppEnvironment
    ): ComponentDescriptor;

    public getActionDescriptors(
        _desc: T | undefined,
        _table: TableGlideType | undefined,
        _schema: SchemaInspector | undefined,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor[] {
        return [];
    }

    public getPropertyAndActionDescriptors(
        desc: T | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screenContext?: ValueProducer,
        appEnvironment?: MinimalAppEnvironment
    ): readonly PropertyDescriptor[] {
        return [
            ...this.getDescriptor(desc, table, ccc, mutatingScreenKind, screenContext, appEnvironment).properties,
            ...this.getActionDescriptors(desc, table, ccc, mutatingScreenKind),
        ];
    }

    public getSpecialCaseDescriptors(_ccc: AppDescriptionContext): readonly ComponentSpecialCaseDescriptor[] {
        return [];
    }

    public get needsColumns(): boolean {
        return true;
    }

    public get isNonEmptyValidationStopper(): boolean {
        return false;
    }

    public get prerequisites(): readonly ComponentPrerequisite[] {
        return [];
    }

    public get isGlideManaged(): boolean {
        return false;
    }

    public get appKinds(): AppKind | "both" {
        if (this.inflate !== undefined) {
            return "both";
        } else {
            return AppKind.App;
        }
    }

    public getSubComponents(_desc: T): readonly ComponentDescription[] | undefined {
        return undefined;
    }

    public getSubComponentTables(
        _desc: T,
        _containingTables: InputOutputTables,
        _schema: SchemaInspector
    ): [InputOutputTables, MutatingScreenKind | undefined] | undefined {
        return undefined;
    }

    public getCaption(desc: T, table: TableGlideType | undefined, ccc: AppDescriptionContext): string | undefined {
        const pd = findCaptionDescriptor(this.getDescriptor(desc, table, ccc, undefined).properties);
        if (pd === undefined) {
            return undefined;
        }
        const propDesc = getPropertyDescription(desc, pd);
        if (propDesc === undefined || propDesc.kind !== PropertyKind.String) return undefined;
        return checkString(propDesc.value);
    }

    public getIsEditor(
        _desc: T | undefined,
        _ccc: AppDescriptionContext,
        _specialCaseDescriptor: ComponentSpecialCaseDescriptor | undefined
    ): boolean {
        return false;
    }

    public getDescriptiveName(desc: T, tables: InputOutputTables, ccc: AppDescriptionContext): [string, string] {
        const descriptor = this.getDescriptor(desc, tables.input, ccc, undefined);

        for (const needRequired of [true, false]) {
            for (const pd of descriptor.properties) {
                const maybeColumnCase = getPropertyDescriptorCaseForKind(PropertyKind.Column, pd);
                if (maybeColumnCase !== undefined) {
                    const columnCase = maybeColumnCase as ColumnPropertyDescriptorCase;
                    if (columnCase.required || !needRequired) {
                        const columnProperty = getSourceColumnProperty(getPropertyDescription(desc, pd));
                        if (columnProperty !== undefined) {
                            const table = getInputOrOutputTable(tables, columnCase.isEditedInApp === true);
                            const resolved = resolveSourceColumn(ccc, columnProperty, table, undefined);
                            return [
                                descriptor.name,
                                definedMap(resolved, r => getTableColumnDisplayName(defined(last(r)).column)) ?? "?",
                            ];
                        }
                    }
                }

                const maybeStringCase = getPropertyDescriptorCaseForKind(PropertyKind.String, pd);
                if (maybeStringCase !== undefined) {
                    const stringCase = maybeStringCase as StringPropertyDescriptorCase;
                    if (stringCase.required || !needRequired) {
                        const stringProperty = getStringProperty(getPropertyDescription(desc, pd));
                        if (stringProperty !== undefined) {
                            return [descriptor.name, stringProperty];
                        }
                    }
                }
            }
        }

        return [descriptor.name, ""];
    }

    public getComponentID(desc: T): string | undefined {
        return desc.componentID;
    }

    public getColumnsUsed(desc: T, tables: InputOutputTables, ccc: AppDescriptionContext): ReadonlyArray<TableColumn> {
        return getColumnsUsedInDescription(
            this.getDescriptor(desc, tables.input, ccc, undefined).properties,
            desc,
            tables,
            ccc
        );
    }

    public getSearchableColumnsUsed(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): ReadonlyArray<TableColumn> {
        const descriptors = this.getDescriptor(desc, tables.input, ccc, undefined).properties;
        return getColumnsUsedInDescription(descriptors, desc, tables, ccc, (_, d) => d.searchable);
    }

    public getScreensUsed(
        _desc: T,
        _schema: SchemaInspector,
        _tables: InputOutputTables | undefined
    ): readonly string[] {
        return [];
    }

    public getTablesForActions(
        tables: InputOutputTables,
        _desc: T,
        _schema: SchemaInspector
    ): [InputOutputTables, boolean] | undefined {
        return [tables, true];
    }

    public getEditedColumns(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        withActions: boolean
    ): EditedColumnsAndTables {
        const descr = this.getDescriptor(desc, tables.input, ccc, mutatingScreenKind);

        let editedColumns = getEditedColumnsForProperties(
            descr.properties,
            desc,
            tables,
            mutatingScreenKind,
            ccc,
            withActions
        );

        // I'd like to get rid of this.  It seems we're only using it in the
        // old component model builder.
        if (withActions) {
            const maybeTablesForActions = this.getTablesForActions(tables, desc, ccc);
            if (maybeTablesForActions !== undefined) {
                const [tablesForActions, isDirect] = maybeTablesForActions;
                const actionsRecord = getActionsForComponent(this, desc, tables.input, ccc, mutatingScreenKind);
                for (const [, actions] of entries(actionsRecord)) {
                    for (const action of actions) {
                        const handler = handlerForActionKind(action.kind);
                        let actionEdited = handler?.getEditedColumns(action, tablesForActions, ccc, mutatingScreenKind);
                        if (!isDirect) {
                            actionEdited = {
                                editedColumns: convertEditedColumnsToIndirect(actionEdited?.editedColumns ?? []),
                                deletedTables: actionEdited?.deletedTables ?? [],
                            };
                        }

                        editedColumns = combineEditedColumnsAndTables(editedColumns, actionEdited);
                    }
                }
            }
        }

        return editedColumns;
    }

    public getDefaultValues(
        _builder: ComponentBuilder,
        _desc: T,
        _tables: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return [];
    }

    public getAdditionalDataEdits(
        _desc: T,
        _tables: InputOutputTables,
        _ccc: AppDescriptionContext,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): AllowedDataEdits | undefined {
        return undefined;
    }

    public needValidation(_desc: T): boolean {
        return false;
    }

    public makeValidationFlag(
        _b: SerializedElementBuilder,
        _desc: T,
        _outputContext: SerializedComponent,
        _table: TableGlideType
    ): SerializedComponent | undefined {
        return undefined;
    }

    public makeNeedsSearch(
        _builder: ComponentBuilder,
        _desc: T,
        _tables: InputOutputTables
    ): SerializedComponent | undefined {
        return undefined;
    }

    public isSearchable(_desc: T): boolean {
        return false;
    }

    public getQuotaBannerRequirements(_desc: T): QuotaBannerRequirements {
        return { needListQuota: false, needMapQuota: false };
    }

    public rewriteAfterReload(
        desc: T,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isRewrite: boolean
    ): T | undefined {
        const newDesc = populateDescription(
            d => this.getPropertyAndActionDescriptors(d, tables.input, ccc, undefined),
            PopulationMode.Rewrite,
            desc,
            desc,
            tables,
            ccc,
            isRewrite,
            mutatingScreenKind
        );
        if (newDesc === undefined) return undefined;

        return {
            ...newDesc,
            ...wrapActionProperties(
                mapActionsRecord(
                    getActionsForComponent(this, desc, tables.input, ccc, mutatingScreenKind),
                    a => rewriteActions(a, tables, ccc, mutatingScreenKind) ?? []
                )
            ),
        };
    }

    protected makeEmptyDescription(): T {
        return makeEmptyComponentDescription(this.kind) as T;
    }

    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T | undefined {
        let desc: T | undefined = this.makeEmptyDescription();
        desc = populateDescription(
            d => this.getPropertyAndActionDescriptors(d, tables.input, iccc, mutatingScreenKind),
            PopulationMode.Default,
            desc,
            desc,
            tables,
            iccc,
            false,
            mutatingScreenKind,
            usedColumns,
            editedColumns,
            true
        );
        return desc;
    }

    public newSpecialCaseComponent(
        _specialCaseDescriptor: ComponentSpecialCaseDescriptor,
        _tables: InputOutputTables,
        _usedColumns: ReadonlySet<TableColumn>,
        _editedColumns: ReadonlySet<TableColumn>,
        _ccc: AppDescriptionContext,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _insideContainer: boolean
    ): T | undefined {
        return panic("Please implement special case component");
    }

    public updateComponent(
        desc: T,
        updates: Partial<T>,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T {
        const newDesc = updateDeleteUndefined(desc, updates);
        return defined(
            populateDescription(
                d => this.getPropertyAndActionDescriptors(d, tables.input, ccc, mutatingScreenKind),
                PopulationMode.Rewrite,
                newDesc,
                newDesc,
                tables,
                ccc,
                true,
                mutatingScreenKind,
                undefined,
                undefined,
                true
            )
        );
    }

    protected duplicateComponentBase(desc: T): T {
        return {
            ...desc,
            ...makeEmptyComponentDescription(desc.kind),
            visibilityFilters: desc.visibilityFilters,
            builderDisplayName: desc.builderDisplayName,
        };
    }

    public async duplicateComponent(
        desc: T,
        copyTables: InputOutputTables,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screensCreated: Set<string>
    ): Promise<T> {
        return await duplicateDescription(
            this.duplicateComponentBase(desc),
            this.getPropertyAndActionDescriptors(desc, copyTables.input, iccc, mutatingScreenKind),
            copyTables,
            iccc,
            mutatingScreenKind,
            screensCreated
        );
    }

    public getErrorAndLink(_desc: T, _table: TableGlideType): ComponentErrorAndLink | undefined {
        return undefined;
    }

    public inflate?(ib: WireRowComponentInflationBackend, desc: T): WireRowComponentHydrator | undefined;
}
